import { Role } from '@app/utils/protected-routes.utils';

import type { Permission } from './permissions';
import { ROLE_HIERARCHY, RoleHierarchyLevels } from './role-hierarchy';
import { Feature, FEATURE_PERMISSIONS, ROLE_PERMISSIONS } from './role-permissions';

/**
 * Resolves all roles including inherited ones using breadth-first traversal
 */
export function resolveInheritedRoles(roles: Role[]): Set<Role> {
  const resolvedRoles = new Set<Role>(roles);
  const queue = [...roles];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const hierarchy = ROLE_HIERARCHY[current];

    if (hierarchy?.inheritsFrom) {
      for (const inheritedRole of hierarchy.inheritsFrom) {
        if (!resolvedRoles.has(inheritedRole)) {
          resolvedRoles.add(inheritedRole);
          queue.push(inheritedRole);
        }
      }
    }
  }

  return resolvedRoles;
}

/**
 * Get all permissions for given roles (including inherited roles)
 */
export function getPermissionsForRoles(roles: Role[]): Set<Permission> {
  const allRoles = resolveInheritedRoles(roles);
  const permissions = new Set<Permission>();

  for (const role of allRoles) {
    const rolePermissions = ROLE_PERMISSIONS[role] || [];
    rolePermissions.forEach((p) => permissions.add(p));
  }

  return permissions;
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(userRoles: Role[] | undefined, permission: Permission): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  const permissions = getPermissionsForRoles(userRoles);
  return permissions.has(permission);
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(userRoles: Role[] | undefined, permissions: Permission[]): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  return permissions.some((p) => hasPermission(userRoles, p));
}

/**
 * Check if user has all the specified permissions
 */
export function hasAllPermissions(userRoles: Role[] | undefined, permissions: Permission[]): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  return permissions.every((p) => hasPermission(userRoles, p));
}

/**
 * Check if user has minimum role level (considering hierarchy)
 */
export function hasMinimumRole(userRoles: Role[] | undefined, minimumRole: Role): boolean {
  if (!userRoles || userRoles.length === 0) return false;

  const allRoles = resolveInheritedRoles(userRoles);
  const minimumLevel = ROLE_HIERARCHY[minimumRole]?.level ?? 0;

  return Array.from(allRoles).some((role) => {
    const roleLevel = ROLE_HIERARCHY[role]?.level ?? 0;
    return roleLevel >= minimumLevel;
  });
}

/**
 * Get the highest role level from user's roles
 */
export function getHighestRoleLevel(userRoles: Role[]): RoleHierarchyLevels {
  return Math.max(...userRoles.map((role) => ROLE_HIERARCHY[role]?.level ?? 0), 0);
}

/**
 * Get the primary (highest level) role from user's roles
 */
export function getPrimaryRole(userRoles: Role[]): Role | undefined {
  if (!userRoles || userRoles.length === 0) return undefined;

  return userRoles.reduce((highest, current) => {
    const currentLevel = ROLE_HIERARCHY[current]?.level ?? 0;
    const highestLevel = ROLE_HIERARCHY[highest!]?.level ?? 0;
    return currentLevel > highestLevel ? current : highest;
  }, userRoles[0]);
}

/**
 * Check if user can access a specific feature
 */
export function canAccessFeature(userRoles: Role[] | undefined, feature: Feature): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  const requiredPermissions = FEATURE_PERMISSIONS[feature];
  if (!requiredPermissions || requiredPermissions.length === 0) return false;
  return hasAnyPermission(userRoles, requiredPermissions);
}

/**
 * Get all features accessible by user
 */
export function getAccessibleFeatures(userRoles: Role[]): Feature[] {
  return Object.values(Feature).filter((feature) => canAccessFeature(userRoles, feature));
}

/**
 * Check if user can manage another user based on roles
 * Used for determining edit/delete permissions in user management
 * Only ADMIN+ roles can manage users
 */
export function canManageUser(managerRoles: Role[], targetRoles: Role[]): boolean {
  const managerLevel = getHighestRoleLevel(managerRoles);
  const targetLevel = getHighestRoleLevel(targetRoles);
  const managerAndTargetsIsAdmin =
    managerLevel === RoleHierarchyLevels.ADMIN_LEVEL && targetLevel === RoleHierarchyLevels.ADMIN_LEVEL;
  const managerAndTargetIsSuperAdmin =
    managerLevel === RoleHierarchyLevels.SUPER_ADMIN_LEVEL && targetLevel === RoleHierarchyLevels.SUPER_ADMIN_LEVEL;

  // Only ADMIN (level 3) and above can manage users
  if (managerLevel < RoleHierarchyLevels.ADMIN_LEVEL) return false;

  // Can manage users at lower level
  if (managerLevel > targetLevel) return true;

  if (managerAndTargetsIsAdmin || managerAndTargetIsSuperAdmin) return true;

  // Super Admin can manage anyone including other Super Admins
  return managerRoles.includes(Role.SUPER_ADMIN);
}

/**
 * Get roles that a user can assign to others
 */
export function getAssignableRoles(userRoles: Role[]): Role[] {
  return Object.entries(ROLE_HIERARCHY)
    .filter(([_, config]) => {
      // Super Admin can assign any role
      if (userRoles.includes(Role.SUPER_ADMIN)) return true;

      // Operator can assign Admin and below
      if (userRoles.includes(Role.OPERATOR)) {
        return config.level <= RoleHierarchyLevels.ADMIN_LEVEL;
      }

      // Admin can assign roles below Admin level
      if (userRoles.includes(Role.ADMIN)) {
        return config.level < RoleHierarchyLevels.ADMIN_LEVEL;
      }

      return false;
    })
    .map(([role]) => role as Role);
}
